import { Fragment, useCallback, useEffect, useState } from 'react';
import { Building2, ChevronDown, ChevronRight, FileText, Loader2, Users } from 'lucide-react';
import { useToast } from '@foundation/hooks/use-toast';
import { useUser } from '@foundation/contextapi/UserContext';
import { getActiveWorkspaceId } from '../../../../header/activeWorkspace';
import {
  fetchActivityLog,
  type ActivityTotals,
  type OrganisationActivity,
} from '../../services/activityLogService';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

export const ActivityLogPage = () => {
  const { toast } = useToast();
  const { user } = useUser();
  const [organisations, setOrganisations] = useState<OrganisationActivity[]>([]);
  const [totals, setTotals] = useState<ActivityTotals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const orgId = getActiveWorkspaceId() ?? undefined;
      const data = await fetchActivityLog(user.id, orgId);
      setOrganisations(data.organisations);
      setTotals(data.totals);
      setForbidden(false);
    } catch (e) {
      if (e instanceof Error && e.message.includes('403')) setForbidden(true);
      else toast({ title: 'Error', description: 'Failed to load activity log', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, toast]);

  useEffect(() => { void load(); }, [load]);

  if (forbidden) {
    return <div className="p-10 text-center text-muted-foreground">Admin access required.</div>;
  }

  const visible = search.trim()
    ? organisations.filter((o) =>
        (o.orgName ?? o.orgId).toLowerCase().includes(search.trim().toLowerCase()),
      )
    : organisations;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Activity Log</h1>
        <p className="text-sm text-muted-foreground">
          Policies created per organisation, with a per-user breakdown inside each organisation.
        </p>
      </div>

      {totals && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" /> Total policies
            </div>
            <div className="text-3xl font-bold">{totals.totalPolicies}</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" /> Organisations
            </div>
            <div className="text-3xl font-bold">{totals.totalOrganisations}</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3.5 w-3.5" /> Active users
            </div>
            <div className="text-3xl font-bold">{totals.totalUsers}</div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search organisations…"
          className="px-3 py-2 rounded-xl border border-border bg-background text-sm w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">
          {organisations.length === 0 ? 'No policies created yet.' : 'No organisations match your search.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border bg-muted/40">
                <th className="p-3 w-8" aria-label="Expand" />
                <th className="p-3">Organisation</th>
                <th className="p-3">Policies created</th>
                <th className="p-3">Users</th>
                <th className="p-3">Last policy</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((org) => {
                const open = expandedOrgId === org.orgId;
                return (
                  <Fragment key={org.orgId}>
                    <tr
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/30"
                      onClick={() => setExpandedOrgId(open ? null : org.orgId)}
                    >
                      <td className="p-3">
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-label={open ? 'Collapse user breakdown' : 'Expand user breakdown'}
                          className="flex items-center"
                        >
                          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="p-3 font-medium">
                        {org.orgName ?? <span className="text-muted-foreground">{org.orgId}</span>}
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#33b864]/10 text-[#33b864]">
                          {org.policyCount}
                        </span>
                      </td>
                      <td className="p-3">{org.users.length}</td>
                      <td className="p-3 whitespace-nowrap">{formatDate(org.lastPolicyAt)}</td>
                    </tr>
                    {open && (
                      <tr className="border-b border-border last:border-0 bg-muted/20">
                        <td className="p-0" colSpan={5}>
                          {org.users.length === 0 ? (
                            <div className="px-12 py-4 text-muted-foreground text-xs">
                              No user attribution recorded for this organisation's policies.
                            </div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-muted-foreground">
                                  <th className="pl-12 pr-3 pt-3 pb-1 font-medium">User</th>
                                  <th className="px-3 pt-3 pb-1 font-medium">Email</th>
                                  <th className="px-3 pt-3 pb-1 font-medium">Policies created</th>
                                  <th className="px-3 pt-3 pb-1 font-medium">Last policy</th>
                                </tr>
                              </thead>
                              <tbody>
                                {org.users.map((u) => (
                                  <tr key={u.userId}>
                                    <td className="pl-12 pr-3 py-1.5">
                                      {u.userName ?? <span className="text-muted-foreground">{u.userId}</span>}
                                    </td>
                                    <td className="px-3 py-1.5">{u.email ?? '—'}</td>
                                    <td className="px-3 py-1.5">{u.policyCount}</td>
                                    <td className="px-3 py-1.5 whitespace-nowrap pb-3">{formatDate(u.lastPolicyAt)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
